import { useInfiniteQuery } from '@tanstack/react-query';
import { searchBooks, type SearchBooksParams, type SearchBooksResponse } from '../api/books';
import { useMemo } from 'react';

interface UseSearchBooksInfiniteOptions {
  enabled?: boolean;
  limit?: number;
}

export const useSearchBooksInfinite = (
  params: Omit<SearchBooksParams, 'page' | 'limit'>,
  options: UseSearchBooksInfiniteOptions = {}
) => {
  const { enabled = true, limit = 10 } = options;

  const query = useInfiniteQuery({
    queryKey: ['booksInfinite', params, limit],
    queryFn: async ({ pageParam = 1 }) => {
      return searchBooks({ ...params, page: pageParam, limit });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.pagination?.has_next) {
        return (lastPage.pagination.page || 1) + 1;
      }
      return undefined;
    },
    enabled,
    staleTime: 30_000,
  });

  const books = useMemo(() => {
    if (!query.data?.pages) return [];
    return query.data.pages.flatMap((page) => page.books || []);
  }, [query.data?.pages]);

  const totalCount = query.data?.pages[0]?.pagination?.total_count ?? 0;

  return {
    books,
    totalCount,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage ?? false,
    fetchNextPage: query.fetchNextPage,
    isError: query.isError,
    error: query.error,
  };
};
